// Client-side full-text search over the bundled documentation.
//
// Every doc's text already ships in the bundle (docs.ts globs the sources with
// ?raw), so search runs entirely in the browser — no backend or search service.
// The index is built once, lazily, on the first query. Matching is exact
// substring/token (all query tokens must appear), ranked title > headings >
// body, and returns a highlighted snippet around the first match.
import { getAllDocs, type DocKind } from './docs';

const PLATFORM_LABELS: Record<string, string> = {
  architecture: 'Architecture',
  azure: 'Azure',
  salesforce: 'Salesforce',
  semarchy: 'Semarchy',
  netsuite: 'NetSuite',
  creatio: 'Creatio',
  pipelines: 'ADO Pipelines',
};

export function platformLabel(platformId: string): string {
  return PLATFORM_LABELS[platformId] ?? platformId.charAt(0).toUpperCase() + platformId.slice(1);
}

/** A run of snippet text; `hit` marks a query match for highlighting. */
export interface SearchSegment {
  text: string;
  hit: boolean;
}

export interface SearchHit {
  slug: string;
  title: string;
  platformId: string;
  platformLabel: string;
  groupLabel: string;
  kind: DocKind;
  score: number;
  snippet: SearchSegment[];
}

interface IndexEntry {
  slug: string;
  title: string;
  platformId: string;
  groupLabel: string;
  kind: DocKind;
  titleLower: string;
  headingsLower: string;
  text: string; // original-case plain text (for snippets)
  textLower: string; // lower-case plain text (for matching)
}

// Reduce a doc's raw content to readable plain text for indexing/snippets.
function toPlainText(content: string, kind: DocKind): string {
  let s = content;
  if (kind === 'html' || kind === 'svg') {
    s = s
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&#39;|&apos;/g, "'")
      .replace(/&quot;/g, '"');
  } else {
    // markdown: keep code text (workflow/API names are worth finding), drop syntax
    s = s
      .replace(/```/g, ' ')
      .replace(/`([^`]*)`/g, '$1')
      .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/^[>\s]*#{1,6}\s+/gm, '')
      .replace(/[*_~>#|]/g, ' ');
  }
  return s.replace(/\s+/g, ' ').trim();
}

function extractHeadings(content: string, kind: DocKind): string[] {
  if (kind === 'md') {
    return [...content.matchAll(/^#{1,6}\s+(.+)$/gm)].map((m) => m[1].trim());
  }
  return [...content.matchAll(/<h[1-6][^>]*>([\s\S]*?)<\/h[1-6]>/gi)].map((m) =>
    m[1].replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim(),
  );
}

let indexCache: IndexEntry[] | null = null;

function getIndex(): IndexEntry[] {
  if (indexCache) return indexCache;
  indexCache = getAllDocs().map((d) => {
    const plain = toPlainText(d.content, d.kind);
    const headings = extractHeadings(d.content, d.kind).join(' ');
    return {
      slug: d.slug,
      title: d.title,
      platformId: d.platformId,
      groupLabel: d.groupLabel,
      kind: d.kind,
      titleLower: d.title.toLowerCase(),
      headingsLower: headings.toLowerCase(),
      text: plain,
      textLower: plain.toLowerCase(),
    };
  });
  return indexCache;
}

function tokenize(q: string): string[] {
  return q
    .toLowerCase()
    .split(/[^a-z0-9]+/i)
    .filter(Boolean);
}

function countOccurrences(haystack: string, needle: string): number {
  if (!needle) return 0;
  let count = 0;
  let i = haystack.indexOf(needle);
  while (i !== -1) {
    count += 1;
    i = haystack.indexOf(needle, i + needle.length);
  }
  return count;
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function makeSnippet(text: string, tokens: string[]): SearchSegment[] {
  if (!text) return [];
  const lower = text.toLowerCase();
  let pos = -1;
  for (const t of tokens) {
    const i = lower.indexOf(t);
    if (i !== -1 && (pos === -1 || i < pos)) pos = i;
  }
  if (pos === -1) pos = 0;
  const start = Math.max(0, pos - 60);
  const end = Math.min(text.length, pos + 180);
  const slice = (start > 0 ? '… ' : '') + text.slice(start, end).trim() + (end < text.length ? ' …' : '');
  return segmentize(slice, tokens);
}

function segmentize(text: string, tokens: string[]): SearchSegment[] {
  const esc = tokens
    .filter(Boolean)
    .map(escapeRe)
    .sort((a, b) => b.length - a.length);
  if (!esc.length) return [{ text, hit: false }];
  const re = new RegExp(`(${esc.join('|')})`, 'ig');
  const out: SearchSegment[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push({ text: text.slice(last, m.index), hit: false });
    out.push({ text: m[0], hit: true });
    last = m.index + m[0].length;
    if (m.index === re.lastIndex) re.lastIndex += 1; // guard against zero-length matches
  }
  if (last < text.length) out.push({ text: text.slice(last), hit: false });
  return out;
}

export function search(query: string, limit = 50): SearchHit[] {
  const tokens = tokenize(query);
  if (!tokens.length) return [];
  const phrase = query.toLowerCase().trim();
  const hits: SearchHit[] = [];
  for (const e of getIndex()) {
    let score = 0;
    let allPresent = true;
    for (const t of tokens) {
      const inTitle = e.titleLower.includes(t);
      const inHead = e.headingsLower.includes(t);
      const bodyCount = countOccurrences(e.textLower, t);
      if (!inTitle && !inHead && bodyCount === 0) {
        allPresent = false;
        break;
      }
      if (inTitle) score += 12;
      if (inHead) score += 5;
      score += Math.min(bodyCount, 5);
    }
    if (!allPresent) continue;
    if (phrase.length > 1) {
      if (e.titleLower.includes(phrase)) score += 15;
      if (e.textLower.includes(phrase)) score += 5;
    }
    hits.push({
      slug: e.slug,
      title: e.title,
      platformId: e.platformId,
      platformLabel: platformLabel(e.platformId),
      groupLabel: e.groupLabel,
      kind: e.kind,
      score,
      snippet: makeSnippet(e.text, tokens),
    });
  }
  hits.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));
  return hits.slice(0, limit);
}
